import { logout } from 'admin/actions/logout';
import { AdminReducerState, AdminThunkDispatch } from 'admin/reducers';
import { SET_LOADING, ThunkActionType } from 'admin/constants/actions';
import { toast } from 'admin/components/common/MessageContainer/toast';
import AdminError from 'admin/components/common/ErrorBoundary/AdminError';

export type UploadParams = {
	apiUrl: string;
	sessionId: string;
	asset?: File;
	url?: string;
	dataUrl?: string;
	method?: string;
};

export const uploadFileHelper = async (params: UploadParams): Promise<any> => {
	const { asset, url, dataUrl } = params;

	if (asset) {
		return new Promise((resolve, reject) => {
			const XHR = new XMLHttpRequest();
			const data = new FormData();

			data.append('asset', asset);

			XHR.addEventListener('load', event => {
				// @ts-expect-error ts-migrate FIXME
				if (JSON.parse(event.target.responseText).success) {
					// @ts-expect-error ts-migrate FIXME
					resolve(JSON.parse(event.target.responseText));
				} else {
					// @ts-expect-error ts-migrate FIXME
					reject(JSON.parse(event.target.responseText));
				}
			});
			XHR.addEventListener('error', e => reject(e));
			XHR.open(params.method || 'POST', `${params.apiUrl}`);
			XHR.setRequestHeader('x-session-token', params.sessionId || '');
			XHR.send(data);
		});
	}

	const response = await fetch(params.apiUrl, {
		method: params.method || 'POST',
		headers: {
			'Content-Type': 'application/json; charset=utf-8',
			'x-session-token': params.sessionId || '',
		},
		body: JSON.stringify({
			data: { url, dataUrl },
		}),
	});

	return response.json();
};

// Common util to read response errors
export const getResponseErrors = (e: IBackendResponseError) => e?.response?.body?.errors;

export const getResponseErrorMsg = (
	e: IBackendResponseError | IBackendError[],
	fallbackMsg: string = 'Something went wrong'
) => {
	const isResponseErrorArr = Array.isArray(e);
	const responseErrorMsg = isResponseErrorArr ? e[0].message : getResponseErrors(e)?.[0]?.message;

	if (typeof responseErrorMsg === 'string') {
		const objectIndex = responseErrorMsg.indexOf('{');
		if (objectIndex !== -1) {
			try {
				const json = JSON.parse(responseErrorMsg.slice(objectIndex));
				return json.response.errors[0].message;
			} catch (_e) {
				return responseErrorMsg;
			}
		}

		return responseErrorMsg;
	}

	return (isResponseErrorArr ? '' : e?.message) || fallbackMsg;
};

// utility function to handle cough 403 error while create/delete/edit story
export const handleStoryNoAccessError = (dispatch: AdminThunkDispatch, error: IBackendResponseError, message = '') => {
	const statusCode = getResponseErrors(error)?.[0]?.statusCode || error.status;
	if (statusCode === 403) {
		dispatch(logout());
		const msg = getResponseErrorMsg(error as IBackendResponseError);
		throw new AdminError(`Failed: ${message} [ ${msg} ]`, { duration: 3 });
	}
};

interface ThunkArgs {
	dispatch: AdminThunkDispatch;
	getState: () => AdminReducerState;
}

interface ThunkActionOptions<Result, ActionParams> {
	type?: ThunkActionType;
	payloadCreator: (args: ActionParams, thunkArgs: ThunkArgs) => Promise<IBackendBody<Result>>;
	onError?: (args: { params: ActionParams; error: any }) => void; // todo: get rid of error any type
	skipStarted?: boolean;
	skipFailed?: boolean;
	skipSucceed?: boolean;
	skipLoading?: boolean;
}

export const createThunkAction = <Result = null, ActionParams = void>({
	type,
	payloadCreator,
	onError,
	skipStarted,
	skipFailed,
	skipSucceed,
	skipLoading,
}: ThunkActionOptions<Result, ActionParams>) => {
	return (params: ActionParams) =>
		async (dispatch: ThunkArgs['dispatch'], getState: ThunkArgs['getState']): Promise<IBackendBody<Result>> => {
			const hasType = type !== undefined;

			// requestStarted
			if (hasType) {
				if (!skipStarted) dispatch({ type: type.PENDING });
				if (!skipLoading) dispatch({ type: SET_LOADING, payload: { [type.PENDING]: true } });
			}

			let result: IBackendBody<Result>;

			try {
				const response = await payloadCreator(params, { dispatch, getState });
				result = response && 'success' in response ? response : { success: true };
			} catch (error) {
				// requestRejected
				const errMsg = getResponseErrorMsg(error as IBackendResponseError);
				if (hasType) {
					if (!skipFailed) dispatch({ type: type.REJECTED, payload: errMsg });
					if (!skipLoading) dispatch({ type: SET_LOADING, payload: { [type.PENDING]: false } });
				}
				toast.error(errMsg, 3);

				onError?.({ error, params });

				// todo: logToService?

				return { success: false, errors: getResponseErrors(error as IBackendResponseError) };
			}

			// requestSucceeded
			if (hasType) {
				if (!skipSucceed) dispatch({ type: type.FULFILLED, payload: result });
				if (!skipLoading) dispatch({ type: SET_LOADING, payload: { [type.PENDING]: false } });
			}

			return result;
		};
};
